<template>
  <div />
</template>

<script>

/**
 * Vue: Boost analytics (boost-analytics)
 * -----------------------------------------------------------------------------
 * Sends events data to Boost for app analytics.
 *
 * @param {Object} analyticsEvent - Analytics event.
 * @param {String} type - Type of event.
 *
 */
import { v4 as uuidv4 } from 'uuid'
import { mapState } from 'vuex'

export default {
  name: 'BoostAnalytics',

  props: {
    analyticsEvent: {
      type: Object,
      default: () => ({}),
    },
    type: {
      type: String,
      default: 'filter',
    },
  },

  emits: {
    'analytics-submitted': (payload) => typeof payload === 'string',
  },

  data() {
    return {
      global: true,
      sessionId: JSON.parse(window.localStorage.getItem('boostPFSSessionId')) || false,
      shop: `${cnvs.credentials.storefront.store}.myshopify.com`,
      token: cnvs.credentials.storefront.token,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      choices: (state) => state.index.choices,
    }),
  },

  watch: {

    /**
     * Watch analytics event to query analytics.
     */
    analyticsEvent() {
      this.queryAnalytics()
    },
  },

  mounted() {
    this.checkSessionId()
  },

  methods: {

    /**
     * Check for Boost session ID and set one if not set.
     * - Boost JS files usually set this under a conventional install
     * - A random string where the only requirement is that it's persistent.
     */
    checkSessionId() {
      if (this.sessionId) {
        return
      }

      localStorage.setItem('boostPFSSessionId', JSON.stringify(uuidv4()))
      this.sessionId = JSON.parse(window.localStorage.getItem('boostPFSSessionId'))
    },

    /**
     * Submit data to Boost Analytics.
     * - This isn't documented, but you can monitor what should be sent by
     *   viewing the Boost demo on their site and watching the Network tab.
     */
    async queryAnalytics() {

      /**
       * a: Boost location: 'filter' or 'search'
       * ct: /cart.js token
       * pid: product ID
       * qs: Boost query.
       * r: document.referrer
       * sid: Boost Session ID.
       * t: UTC ISO 8601 timestamp.
       * tid: Shop URL
       * u: Boost events: view_product, add_to_cart
       */

      const defaultData = {
        a: this.type,
        ct: this.token,
        qs: this.choices,
        r: document.referrer,
        sid: this.sessionId,
        t: new Date().toISOString(),
        tid: this.shop,
      }

      const combined = {
        ...defaultData,
        pid: this.analyticsEvent.pid,
        u: this.analyticsEvent.u,
      }

      /**
       * Remove any falsey values.
       */
      for (const [key, value] of Object.entries(combined)) {
        if (!value) {
          delete combined[key]
        }
      }

      if (combined.u === 'add_to_cart') {
        delete combined.a
        delete combined.r
        delete combined.t
      }

      /**
       * Make query.
       */
      const headers = {
        'Content-Type': 'application/json',
      }

      const body = JSON.stringify(combined)

      try {
        await fetch('https://lambda.mybcapps.com/e', {
          body,
          headers,
          method: 'POST',
        })

      } catch (error) {
        reportError('Failed to report Boost analytics', error)
      }

      this.$emit('analytics-submitted', this.analyticsEvent.url)
    },
  },
}
</script>
